import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { LayoutDashboard, Zap, Activity, Smartphone, ArrowRight } from "lucide-react";

const SHOTS = {
  laptop: "https://static.prod-images.emergentagent.com/jobs/f66e0e14-b56e-4de5-b0fd-8ad35555866a/images/848ccb61fb19c4c7ec59d9d7460e3c1bb3fa97a9a217e33f4cc273581ab57823.png",
  mobile: "https://static.prod-images.emergentagent.com/jobs/f66e0e14-b56e-4de5-b0fd-8ad35555866a/images/cdf086e1931e623529ef7e8cfd1cde11f599f56259819dada952a3284439d9bc.png",
};

const POINTS = [
  { icon: LayoutDashboard, key: "p1" },
  { icon: Zap, key: "p2" },
  { icon: Activity, key: "p3" },
  { icon: Smartphone, key: "p4" },
];

export function PlatformShowcaseSection() {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <section id="apercu" className="relative py-24 lg:py-32 overflow-hidden" data-testid="platform-showcase-section">
      <div className="pointer-events-none absolute top-1/4 right-0 w-[600px] h-[600px] pg-radial-gold opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Texte */}
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">{t("showcase.eyebrow")}</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              {t("showcase.titleA")} <span className="text-[#EAB308]">{t("showcase.titleB")}</span>
            </h2>
            <p className="mt-4 text-white/60 text-lg leading-relaxed">
              {t("showcase.desc")}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {POINTS.map((p, i) => (
                <motion.div
                  key={p.key}
                  data-testid={`showcase-point-${i}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                >
                  <p.icon className="w-6 h-6 text-[#EAB308]" />
                  <h3 className="mt-3 font-semibold text-white">{t(`showcase.${p.key}t`)}</h3>
                  <p className="mt-1 text-sm text-white/55">{t(`showcase.${p.key}`)}</p>
                </motion.div>
              ))}
            </div>

            <button
              data-testid="showcase-cta"
              onClick={() => navigate("/login")}
              className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-[#EAB308] px-7 py-4 text-base font-bold text-black transition-transform hover:-translate-y-0.5"
            >
              {t("showcase.cta")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Visuels */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="group relative rounded-2xl border border-white/10 bg-[#0a0a0a] p-2 shadow-2xl transition-transform duration-500 hover:-translate-y-1">
              <img
                src={SHOTS.laptop}
                alt="Aperçu du tableau de bord PROGÈRE sur ordinateur"
                className="w-full rounded-xl"
                loading="lazy"
              />
              <div className="pointer-events-none absolute -inset-8 -z-10 pg-radial-red opacity-60" />
            </div>
            <motion.img
              src={SHOTS.mobile}
              alt="Aperçu de l'application mobile PROGÈRE"
              loading="lazy"
              className="absolute -bottom-8 -left-6 w-28 sm:w-36 rounded-2xl border border-white/10 shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
